import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../../use-stores";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import { dailyTasksStageUi, defaultSortByForStage } from "./DailyTasksStageUi";
import {
  DailyTasksFilters,
  dailyTasksFiltersToSearchParams,
  defaultDailyTasksFilters,
  EXCLUDED_FROM_SOURCING_OPTIONS,
  INCLUDE_RUNNING_OPTIONS,
  parseQueryParamsForDailyTasksFilters,
  PROJECT_STATUS_OPTIONS,
  projectStatusToOption,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasksFilters";
import {
  DAILY_TASKS_STAGE_TABS,
  DailyTasksStage,
  dailyTasksFamilyBlurb,
  INWARD_DATE_FIELD_OPTIONS,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import { trackDailyTasksAction } from "../../../../../../analytics/dailyTasksTracking";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { DateRangePickerNew } from "../../../../../../views/components/common/textFields/DatePickerRangeNew";
import { listingStatusTabsStyles } from "../../../../../../styles/listingStatusTabsSx";
import { DailyTasksHint, DAILY_TASKS_HINTS } from "./DailyTasksHint";

const SEARCH_DEBOUNCE_MS = 400;

const isInwardDateStage = (stage: DailyTasksStage): boolean =>
  stage === "raw_inward" || stage === "print_inward";

const PROJECT_CREATED_DATE_FIELD: FilterValue[] = [
  { id: "start_date", value: "Project approved" },
];

const coerceDateFieldForStage = (
  stage: DailyTasksStage,
  dateField: FilterValue[],
  canViewDispatchDate: boolean,
): Partial<DailyTasksFilters> => {
  const currentId = dateField[0]?.id;
  if (isInwardDateStage(stage)) {
    const allowed = INWARD_DATE_FIELD_OPTIONS.some(
      (option) => option.id === currentId,
    );
    if (!allowed) {
      return {
        date_field: PROJECT_CREATED_DATE_FIELD,
        ...(canViewDispatchDate ? {} : { dispatch_bucket: [] as FilterValue[] }),
      };
    }
    const match = INWARD_DATE_FIELD_OPTIONS.find(
      (option) => option.id === currentId,
    );
    return {
      date_field: match
        ? [{ id: match.id, value: match.value }]
        : PROJECT_CREATED_DATE_FIELD,
      ...(canViewDispatchDate ? {} : { dispatch_bucket: [] as FilterValue[] }),
    };
  }
  // Every other tab: project approved only (ignore dispatch / inward fields in the URL).
  return {
    date_field: PROJECT_CREATED_DATE_FIELD,
    ...(canViewDispatchDate ? {} : { dispatch_bucket: [] as FilterValue[] }),
  };
};

type DatePreset = "7d" | "30d" | "90d" | "6m" | "fy";

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "6m", label: "6m" },
  { id: "fy", label: "FY" },
];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const getIndianFyStart = (today: Dayjs): Dayjs => {
  const fyYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return dayjs(`${fyYear}-04-01`).startOf("day");
};

const getPresetRange = (preset: DatePreset): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().endOf("day");
  if (preset === "7d") {
    return { start: dayjs().subtract(6, "day").startOf("day"), end };
  }
  if (preset === "90d") {
    return { start: dayjs().subtract(89, "day").startOf("day"), end };
  }
  if (preset === "6m") {
    return { start: dayjs().subtract(6, "month").startOf("day"), end };
  }
  if (preset === "fy") {
    return { start: getIndianFyStart(dayjs()), end };
  }
  return { start: dayjs().subtract(29, "day").startOf("day"), end };
};

const matchDatePreset = (start: Dayjs, end: Dayjs): DatePreset | null => {
  const presets: DatePreset[] = ["7d", "30d", "90d", "6m", "fy"];
  for (const preset of presets) {
    const range = getPresetRange(preset);
    if (start.isSame(range.start, "day") && end.isSame(range.end, "day")) {
      return preset;
    }
  }
  return null;
};

const toFilterValues = (
  items: Array<{ id: number | string; value: string }>,
): FilterValue[] => items.map((item) => ({ id: String(item.id), value: item.value }));

interface DailyTasksTatFiltersProps {
  store: DailyTasksStore;
}

const pillButtonSx = { minHeight: 32, px: 1.5 };

const FilterField = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          color={theme.palette.customColors.greyTextColor}
        >
          {label}
        </CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

const DailyTasksTatFilters: React.FC<DailyTasksTatFiltersProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const { orderStore, customerStore } = useStores();
    const { canViewDispatchDate } = useDailyTasksFieldPermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [inlineError, setInlineError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [draftStart, setDraftStart] = useState<Dayjs>(
      store.filters.start_date,
    );
    const [draftEnd, setDraftEnd] = useState<Dayjs>(store.filters.end_date);
    const isInitialMount = useRef(true);
    const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const customerSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const orderTypeOptions = useMemo(
      () => toFilterValues(store.filterOptions.order_types),
      [store.filterOptions.order_types],
    );
    const salesPocOptions = useMemo(
      () => toFilterValues(store.filterOptions.sales_pocs),
      [store.filterOptions.sales_pocs],
    );
    const pmPocOptions = useMemo(
      () => toFilterValues(store.filterOptions.pm_pocs),
      [store.filterOptions.pm_pocs],
    );
    const orderOptions = useMemo(
      () =>
        (orderStore.orderIds ?? []).map((item) => ({
          id: String(item),
          value: String(item),
        })),
      [orderStore.orderIds],
    );
    const customerOptions = useMemo(
      () =>
        (customerStore.customers ?? []).map((customer) => ({
          id: String(customer.id),
          value: customer.name ?? String(customer.id),
        })),
      [customerStore.customers],
    );
    const inwardDateFieldOptions = useMemo(
      () =>
        INWARD_DATE_FIELD_OPTIONS.map((option) => ({
          id: option.id,
          value: option.value,
        })),
      [],
    );

    const activePreset = matchDatePreset(draftStart, draftEnd);
    const displayedError = inlineError || store.summaryError;

    const fetchData = useCallback(
      async (nextFilters: DailyTasksFilters) => {
        setInlineError(null);
        try {
          await Promise.allSettled([
            store.fetchSummary(nextFilters),
            store.fetchQueue(nextFilters),
          ]);
        } catch (error) {
          setInlineError(
            getErrorMessage(error, "Failed to fetch daily tasks."),
          );
        }
      },
      [store],
    );

    useEffect(() => {
      const init = async () => {
        try {
          await store.fetchFilterOptions();
          const hydrated = parseQueryParamsForDailyTasksFilters(searchParams);
          setSearchTerm(hydrated.search);
          setDraftStart(hydrated.start_date);
          setDraftEnd(hydrated.end_date);
          const hasOrderTypeInUrl = Boolean(
            searchParams.get("order_type_id")?.trim(),
          );
          const nextFilters: DailyTasksFilters = {
            ...hydrated,
            order_type_id: hasOrderTypeInUrl
              ? hydrated.order_type_id
              : toFilterValues(store.filterOptions.order_types),
            ...coerceDateFieldForStage(
              hydrated.stage,
              hydrated.date_field,
              canViewDispatchDate,
            ),
          };
          store.setFilters(nextFilters);
          trackDailyTasksAction("dashboard_view", nextFilters);
          await fetchData(nextFilters);
        } catch (error) {
          setInlineError(
            getErrorMessage(error, "Failed to load filters."),
          );
        }
      };
      void init();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      setSearchParams(dailyTasksFiltersToSearchParams(store.filters), {
        replace: true,
      });
    }, [store.filters, setSearchParams]);

    const applyFilters = (overrides: Partial<DailyTasksFilters> = {}) => {
      try {
        const merged: DailyTasksFilters = {
          ...store.filters,
          ...overrides,
          search: (overrides.search ?? searchTerm).trim(),
          start_date: overrides.start_date ?? draftStart,
          end_date: overrides.end_date ?? draftEnd,
          page_no: 1,
        };
        const next: DailyTasksFilters = {
          ...merged,
          ...coerceDateFieldForStage(
            merged.stage,
            merged.date_field,
            canViewDispatchDate,
          ),
        };
        store.setFilters(next);
        void fetchData(next);
        return next;
      } catch (error) {
        console.error(error);
        setInlineError("Failed to apply filters.");
        return null;
      }
    };

    const handlePresetClick = (preset: DatePreset) => {
      const range = getPresetRange(preset);
      setDraftStart(range.start);
      setDraftEnd(range.end);
      trackDailyTasksAction("date_preset_select", store.filters, {
        preset,
        context: { date_preset: preset },
      });
    };

    const handleRangeSelect = (range: {
      startDate: Dayjs | null;
      endDate: Dayjs | null;
    }) => {
      if (range.startDate) setDraftStart(range.startDate.startOf("day"));
      if (range.endDate) setDraftEnd(range.endDate.endOf("day"));
      const start = range.startDate?.startOf("day") ?? draftStart;
      const end = range.endDate?.endOf("day") ?? draftEnd;
      trackDailyTasksAction(
        "date_range_change",
        {
          ...store.filters,
          start_date: start,
          end_date: end,
        },
        {
          context: {
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
          },
        },
      );
    };

    const handleOrderSearch = (searchText?: string) => {
      if (orderSearchTimerRef.current) {
        clearTimeout(orderSearchTimerRef.current);
      }
      if (searchText && searchText.length > 0) {
        orderSearchTimerRef.current = setTimeout(() => {
          orderStore.searchOrderIds(searchText).catch(() => {
            setInlineError("Failed to search order IDs.");
          });
        }, SEARCH_DEBOUNCE_MS);
      }
    };

    const handleCustomerSearch = (searchText?: string) => {
      if (customerSearchTimerRef.current) {
        clearTimeout(customerSearchTimerRef.current);
      }
      if (searchText && searchText.length > 0) {
        customerSearchTimerRef.current = setTimeout(() => {
          customerStore
            .searchCustomers({
              search_text: searchText,
              page_no: 1,
              limit: 20,
            })
            .catch(() => {
              setInlineError("Failed to search customers.");
            });
        }, SEARCH_DEBOUNCE_MS);
      }
    };

    const handleClear = () => {
      const defaults = defaultDailyTasksFilters();
      setSearchTerm("");
      setDraftStart(defaults.start_date);
      setDraftEnd(defaults.end_date);
      const next = applyFilters({
        ...defaults,
        stage: store.filters.stage,
        sort_by: defaultSortByForStage(store.filters.stage),
        order_type_id: toFilterValues(store.filterOptions.order_types),
        date_field: PROJECT_CREATED_DATE_FIELD,
        dispatch_bucket: canViewDispatchDate ? defaults.dispatch_bucket : [],
      });
      if (next) {
        trackDailyTasksAction("filter_clear", next);
      }
    };

    const showInwardDateField = isInwardDateStage(store.filters.stage);

    return (
      <Paper
        sx={{
          mt: 2,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CustomTypography variant="heading3">Tasks</CustomTypography>
              <DailyTasksHint text={DAILY_TASKS_HINTS.page} />
            </Box>
            <CustomTypography
              variant="body2"
              color={customColors.greyTextColor}
              sx={{ mt: 0.5 }}
            >
              {dailyTasksFamilyBlurb(store.filters.stage)}
            </CustomTypography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => {
                const nextOpen = !filtersOpen;
                setFiltersOpen(nextOpen);
                trackDailyTasksAction("search_panel_toggle", store.filters, {
                  expanded: nextOpen,
                  context: { filters_expanded: nextOpen },
                });
              }}
              aria-label={filtersOpen ? "Collapse filters" : "Expand filters"}
            >
              {filtersOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        <Tabs
          value={store.filters.stage}
          onChange={(_event, stage: DailyTasksStage) => {
            const tab = DAILY_TASKS_STAGE_TABS.find((item) => item.id === stage);
            if (!tab?.enabled || stage === store.filters.stage) return;
            const previousStage = store.filters.stage;
            const next = applyFilters({
              stage,
              mockup_work_state: [],
              work_bucket: [],
              sort_by: defaultSortByForStage(stage),
            });
            if (next) {
              trackDailyTasksAction("stage_tab_change", next, {
                stage,
                previousStage,
                context: {
                  stage,
                  previous_stage: previousStage,
                },
              });
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={(tabTheme) => ({
            ...listingStatusTabsStyles(tabTheme),
            mt: 1.5,
          })}
        >
          {DAILY_TASKS_STAGE_TABS.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              disabled={!tab.enabled}
              label={
                <Box
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                >
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    {tab.label}
                  </Box>
                  {!tab.enabled ? (
                    <Chip
                      label="Soon"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        bgcolor: customColors.veryLightGray,
                        color: customColors.greyTextColor,
                      }}
                    />
                  ) : null}
                </Box>
              }
            />
          ))}
        </Tabs>

        <Collapse in={filtersOpen}>
          <Grid2 container spacing={2} columns={12} sx={{ mt: 2 }}>
            <Grid2 size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box sx={{ flex: "0 0 auto" }}>
                  <FilterField
                    label="Date"
                    hint={
                      DAILY_TASKS_HINTS[
                        dailyTasksStageUi(store.filters.stage).dateHint
                      ]
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {DATE_PRESETS.map((preset) => (
                        <AppButton
                          key={preset.id}
                          label={preset.label}
                          variant={
                            activePreset === preset.id
                              ? "primary"
                              : "transparentPrimary"
                          }
                          onClick={() => handlePresetClick(preset.id)}
                          customStyles={pillButtonSx}
                        />
                      ))}
                      <Box sx={{ width: { xs: "100%", sm: 220 }, flexShrink: 0 }}>
                        <DateRangePickerNew
                          startDate={draftStart}
                          endDate={draftEnd}
                          onRangeSelect={handleRangeSelect}
                        />
                      </Box>
                    </Box>
                  </FilterField>
                </Box>
                <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                  <FilterField label="Search">
                    <CustomTextField
                      width="100%"
                      placeholder="Order, brand, product"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          const next = applyFilters({
                            search: searchTerm.trim(),
                          });
                          if (next) {
                            trackDailyTasksAction("filter_apply", next, {
                              searchTerm: searchTerm.trim(),
                              context: {
                                search_term_len: searchTerm.trim().length,
                              },
                            });
                          }
                        }
                      }}
                    />
                  </FilterField>
                </Box>
                {showInwardDateField ? (
                  <Box sx={{ flex: "0 1 220px", minWidth: 180 }}>
                    <FilterField
                      label="Date field"
                      hint={DAILY_TASKS_HINTS.dateFieldInward}
                    >
                      <AppSearchDropdownMulti
                        title="Date field"
                        list={inwardDateFieldOptions}
                        value={store.filters.date_field}
                        local
                        isMultiSelect={false}
                        inputHeight="37px"
                        borderRadius="4px"
                        handleSelect={(selected) => {
                          const date_field =
                            selected && selected.length > 0
                              ? selected
                              : PROJECT_CREATED_DATE_FIELD;
                          store.updateFilters((prev) => ({
                            ...prev,
                            date_field,
                          }));
                          trackDailyTasksAction(
                            "filter_date_field_change",
                            store.filters,
                            {
                              context: {
                                date_field: String(date_field[0]?.id ?? ""),
                              },
                            },
                          );
                        }}
                      />
                    </FilterField>
                  </Box>
                ) : null}
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="Order type">
                <AppSearchDropdownMulti
                  title="Order type"
                  list={orderTypeOptions}
                  value={store.filters.order_type_id}
                  local
                  isMultiSelect
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    store.updateFilters((prev) => ({
                      ...prev,
                      order_type_id: selected ?? [],
                    }));
                    trackDailyTasksAction(
                      "filter_order_type_change",
                      store.filters,
                      {
                        context: {
                          order_type_count: selected?.length ?? 0,
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="Order ID">
                <AppSearchDropdownMulti
                  title="Order ID"
                  list={orderOptions}
                  value={store.filters.order_id}
                  isMultiSelect
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSearch={handleOrderSearch}
                  handleSelect={(selected) => {
                    store.updateFilters((prev) => ({
                      ...prev,
                      order_id: selected ?? [],
                    }));
                    trackDailyTasksAction(
                      "filter_order_id_change",
                      store.filters,
                      {
                        context: {
                          order_id_count: selected?.length ?? 0,
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="Customer">
                <AppSearchDropdownMulti
                  title="Customer"
                  list={customerOptions}
                  value={store.filters.customer_id}
                  isMultiSelect
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSearch={handleCustomerSearch}
                  handleSelect={(selected) => {
                    store.updateFilters((prev) => ({
                      ...prev,
                      customer_id: selected ?? [],
                    }));
                    trackDailyTasksAction(
                      "filter_customer_change",
                      store.filters,
                      {
                        context: {
                          customer_id_count: selected?.length ?? 0,
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="PM POC">
                <AppSearchDropdownMulti
                  title="PM POC"
                  list={pmPocOptions}
                  value={store.filters.pm_poc_id}
                  local
                  isMultiSelect
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    store.updateFilters((prev) => ({
                      ...prev,
                      pm_poc_id: selected ?? [],
                    }));
                    trackDailyTasksAction(
                      "filter_pm_poc_change",
                      store.filters,
                      {
                        context: { pm_poc_count: selected?.length ?? 0 },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="Sales POC">
                <AppSearchDropdownMulti
                  title="Sales POC"
                  list={salesPocOptions}
                  value={store.filters.sales_poc_id}
                  local
                  isMultiSelect
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    store.updateFilters((prev) => ({
                      ...prev,
                      sales_poc_id: selected ?? [],
                    }));
                    trackDailyTasksAction(
                      "filter_sales_poc_change",
                      store.filters,
                      {
                        context: { sales_poc_count: selected?.length ?? 0 },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label="Project status">
                <AppSearchDropdownMulti
                  title="Project status"
                  list={PROJECT_STATUS_OPTIONS}
                  value={projectStatusToOption(store.filters.project_status)}
                  local
                  isMultiSelect={false}
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    const id = selected[0]?.id;
                    const project_status =
                      id === "archived"
                        ? true
                        : id === "both"
                          ? null
                          : false;
                    store.updateFilters((prev) => ({
                      ...prev,
                      project_status,
                    }));
                    trackDailyTasksAction(
                      "filter_project_status_change",
                      store.filters,
                      {
                        context: {
                          project_status:
                            id === "archived"
                              ? "archived"
                              : id === "both"
                                ? "both"
                                : "active",
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField
                label="Excluded from sourcing"
                hint={DAILY_TASKS_HINTS.excludedFromSourcing}
              >
                <AppSearchDropdownMulti
                  title="Excluded from sourcing"
                  list={EXCLUDED_FROM_SOURCING_OPTIONS}
                  value={EXCLUDED_FROM_SOURCING_OPTIONS.filter(
                    (item) =>
                      item.id === String(store.filters.excluded_orders),
                  )}
                  local
                  isMultiSelect={false}
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    const excluded_orders = selected[0]?.id === "true";
                    store.updateFilters((prev) => ({
                      ...prev,
                      excluded_orders,
                    }));
                    trackDailyTasksAction(
                      "filter_excluded_orders_change",
                      store.filters,
                      {
                        context: { excluded_orders },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField
                label="Include running"
                hint={DAILY_TASKS_HINTS.includeRunning}
              >
                <AppSearchDropdownMulti
                  title="Include running"
                  list={INCLUDE_RUNNING_OPTIONS}
                  value={INCLUDE_RUNNING_OPTIONS.filter(
                    (item) =>
                      item.id === String(store.filters.include_running),
                  )}
                  local
                  isMultiSelect={false}
                  inputHeight="37px"
                  borderRadius="4px"
                  handleSelect={(selected) => {
                    const include_running = selected[0]?.id === "true";
                    store.updateFilters((prev) => ({
                      ...prev,
                      include_running,
                    }));
                    trackDailyTasksAction(
                      "filter_include_running_change",
                      store.filters,
                      {
                        context: { include_running },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              sx={{ display: "flex", alignItems: "flex-end" }}
            >
              <FilterField label={"\u00A0"}>
                <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={handleClear}
                    disabled={store.summaryLoading}
                    customStyles={{ width: "100%", minHeight: 37 }}
                  />
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={() => {
                      const next = applyFilters();
                      if (next) {
                        trackDailyTasksAction("filter_apply", next);
                      }
                    }}
                    disabled={store.summaryLoading}
                    customStyles={{ width: "100%", minHeight: 37 }}
                  />
                </Box>
              </FilterField>
            </Grid2>
          </Grid2>
        </Collapse>

        {displayedError ? (
          <Box
            mt={2}
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="4rem"
            textAlign="center"
            border="1px dashed"
            borderColor="divider"
            borderRadius={2}
            px={2}
            py={1.5}
          >
            <CustomTypography
              variant="heading6"
              fontWeight={500}
              color={customColors.errorRed}
            >
              {displayedError}
            </CustomTypography>
          </Box>
        ) : null}
      </Paper>
    );
  },
);

export default DailyTasksTatFilters;
